import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { AppHeader } from '../components/AppHeader';
import { Spinner } from '../components/Spinner';
import { ActionSheet } from '../components/BottomSheet';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { AccountMenu } from '../auth/AccountMenu';
import { useAgendas, useDeleteAgenda } from '../api/queries';
import { getAccentColor, type AgendaSummary } from '../types/agenda';
import { useTenant } from '../auth/TenantProvider';

export function ListScreen() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useAgendas();
  const del = useDeleteAgenda();
  const { activeTenant } = useTenant();

  const [menuFor, setMenuFor] = useState<AgendaSummary | null>(null);
  const [confirmDel, setConfirmDel] = useState<AgendaSummary | null>(null);

  const agendas = Array.isArray(data?.agendas) ? data.agendas : [];
  const permissions = data?.permissions;

  const groups = useMemo(() => {
    if (!activeTenant) {
      return {};
    }
    const result: Record<string, AgendaSummary[]> = {};
    activeTenant.meetingTypes.forEach((mt) => {
      result[mt.key] = agendas.filter((s) => s.meetingType === mt.key);
    });
    return result;
  }, [agendas, activeTenant]);

  const header = (
    <AppHeader title="Agendas" actions={<AccountMenu />} />
  );

  return (
    <Screen header={header}>
      {isLoading ? (
        <Spinner label="Loading agendas…" />
      ) : isError ? (
        <div className="state-center">
          <div className="state-icon">⚠️</div>
          <h2>Couldn't load agendas</h2>
          <p>{(error as Error)?.message}</p>
          <button className="btn" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      ) : agendas.length === 0 && (permissions?.readTypes.length ?? 1) === 0 ? (
        <div className="state-center">
          <div className="state-icon">🔒</div>
          <h2>No permissions configured</h2>
          <p>Contact the administrator to enable access.</p>
        </div>
      ) : agendas.length === 0 ? (
        <div className="state-center">
          <div className="state-icon">🗓️</div>
          <h2>No agendas yet</h2>
          <p>Create your first agenda to get started.</p>
          <button className="btn btn-primary" onClick={() => navigate('/new')}>
            New Agenda
          </button>
        </div>
      ) : (
        <div className="list">
          {activeTenant && activeTenant.meetingTypes.map((mtConfig) => {
            const hasAgendas = (groups[mtConfig.key] || []).length > 0;
            const hasPermission = permissions?.readTypes.includes(mtConfig.key) ?? true;
            return (
              <div key={mtConfig.key}>
                {(hasAgendas || !hasPermission) && (
                  <>
                    <div className="list-section-header">{mtConfig.label}</div>
                    {hasAgendas ? (
                      groups[mtConfig.key].map((s) => (
                        <AgendaCard key={s.id} summary={s} tenant={activeTenant} onMenu={() => setMenuFor(s)} />
                      ))
                    ) : (
                      <p className="list-perm-note">Not visible due to permissions</p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(permissions?.writeTypes.length ?? 1) > 0 && (
        <button className="fab" onClick={() => navigate('/new')}>
          <PlusIcon /> New Agenda
        </button>
      )}

      <ActionSheet
        open={menuFor !== null}
        onClose={() => setMenuFor(null)}
        title={menuFor?.title}
        actions={
          menuFor
            ? [
                { label: 'Edit', onClick: () => navigate(`/agenda/${menuFor.id}/edit`) },
                { label: 'Publish…', onClick: () => navigate(`/agenda/${menuFor.id}/publish`) },
                { label: 'Delete', danger: true, onClick: () => setConfirmDel(menuFor) },
              ]
            : []
        }
      />

      <ConfirmDialog
        open={confirmDel !== null}
        title="Delete agenda?"
        message={confirmDel ? `Delete "${confirmDel.title}"? This cannot be undone.` : ''}
        confirmLabel="Delete"
        danger
        onConfirm={() => {
          if (confirmDel) del.mutate(confirmDel.id);
          setConfirmDel(null);
        }}
        onClose={() => setConfirmDel(null)}
      />
    </Screen>
  );
}

function AgendaCard({
  summary,
  tenant,
  onMenu,
}: {
  summary: AgendaSummary;
  tenant?: any;
  onMenu: () => void;
}) {
  const navigate = useNavigate();
  const accent = getAccentColor(summary.meetingType, tenant);

  return (
    <button className="agenda-card" onClick={() => navigate(`/agenda/${summary.id}/edit`)}>
      <span className="agenda-card-accent" style={{ background: accent }} />
      <span className="agenda-card-body">
        <span className="agenda-card-title">{summary.title}</span>
        <span className="agenda-card-meta">
          {summary.date}
          {summary.location ? ` · ${summary.location}` : ''}
        </span>
      </span>
      {summary.totalMinutes > 0 && (
        <span
          className="agenda-card-duration"
          style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)`, color: accent }}
        >
          {summary.totalMinutes} min
        </span>
      )}
      <span
        className="card-menu-btn"
        role="button"
        tabIndex={0}
        aria-label="Actions"
        onClick={(e) => {
          e.stopPropagation();
          onMenu();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            onMenu();
          }
        }}
      >
        <DotsIcon />
      </span>
    </button>
  );
}

function PlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}
